import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast, Bounce } from 'react-toastify';
import CustomStepBadge from '../../../components/OnboardingComponents/CustomStepBadge';
import CustomStatCard from '../../../components/OnboardingComponents/CustomStatCard';
import CustomSection from '../../../components/OnboardingComponents/CustomSection';
import CustomEntryCard from '../../../components/OnboardingComponents/CustomEntryCard';
import CustomButton from '../../../components/OnboardingComponents/CustomButton';
import { ICONS } from '../../../components/OnboardingComponents/icon';
import useCategories from '../../../hooks/useCategories';
import { del } from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import { toastApiError } from '../../../helpers/apiError';
import { deleteOnboardingProject } from './onboardingProjectHelpers';
import {
  removeOngoingRole,
  removePastProject,
  removeCurrentProject,
  removeFutureProject,
  completeOnboarding,
} from '../../../store/slices/onboardingSlice';

const Step4Review = ({ onNext }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(null);
  const roles = useSelector((state) => state.onboarding?.ongoingRoles || []);
  const currentProjects = useSelector(
    (state) => state.onboarding?.currentProjects || []
  );
  const pastProjects = useSelector(
    (state) => state.onboarding?.pastProjects || []
  );
  const futureProjects = useSelector(
    (state) => state.onboarding?.futureProjects || []
  );
  const { getCategoryLabel } = useCategories();

  const totalProjects =
    currentProjects.length + pastProjects.length + futureProjects.length;
  const activeProjects = currentProjects.length;

  const categoryLabels = (values = []) =>
    (Array.isArray(values) ? values : [])
      .map(getCategoryLabel)
      .filter(Boolean)
      .join(', ');

  const handleEditRole = (index) => {
    const role = roles[index];
    if (!role?.id) return;
    navigate(`/onboarding/ongoing-roles?from=review&edit=${role.id}`);
  };

  const handleEditCurrentProject = (index) => {
    const project = currentProjects[index];
    if (!project?.id) return;
    navigate(`/onboarding/current-projects?from=review&edit=${project.id}`);
  };

  const handleEditPastProject = (index) => {
    const project = pastProjects[index];
    if (!project?.id) return;
    navigate(`/onboarding/past-projects?from=review&edit=${project.id}`);
  };

  const handleEditFutureProject = (index) => {
    const project = futureProjects[index];
    if (!project?.id) return;
    navigate(`/onboarding/future-projects?from=review&edit=${project.id}`);
  };

  const handleDeleteRole = async (index) => {
    const role = roles[index];
    if (!role?.id) {
      dispatch(removeOngoingRole(index));
      return;
    }

    setDeleting({ type: 'role', index });
    try {
      const res = await del(`${endpoints.onGoingRole}/${role.id}`);
      if (res?.data?.success) {
        toast.success(res?.data?.message || 'Role deleted', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        dispatch(removeOngoingRole(index));
      } else {
        toastApiError(res, 'Failed to delete role');
      }
    } catch (error) {
      toastApiError(error, 'Failed to delete role');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCurrentProject = async (index) => {
    setDeleting({ type: 'current', index });
    const project = currentProjects[index];
    if (project?.id) {
      const deleted = await deleteOnboardingProject(project.id);
      if (!deleted) {
        setDeleting(null);
        return;
      }
    }
    dispatch(removeCurrentProject(index));
    setDeleting(null);
  };

  const handleDeletePastProject = async (index) => {
    setDeleting({ type: 'past', index });
    const project = pastProjects[index];
    if (project?.id) {
      const deleted = await deleteOnboardingProject(project.id);
      if (!deleted) {
        setDeleting(null);
        return;
      }
    }
    dispatch(removePastProject(index));
    setDeleting(null);
  };

  const handleDeleteFutureProject = async (index) => {
    setDeleting({ type: 'future', index });
    const project = futureProjects[index];
    if (project?.id) {
      const deleted = await deleteOnboardingProject(project.id);
      if (!deleted) {
        setDeleting(null);
        return;
      }
    }
    dispatch(removeFutureProject(index));
    setDeleting(null);
  };

  const handleContinue = () => {
    dispatch(completeOnboarding());
    onNext?.();
  };

  return (
    <div className="amok-onboarding-step">
      <div className="amok-onboarding-step__header">
        <CustomStepBadge tone="accent">Review</CustomStepBadge>
      </div>

      <h2 className="amok-onboarding-step__title">You&rsquo;re all set</h2>
      <p className="amok-onboarding-step__subtitle">
        Here&rsquo;s how your profile looks. You can edit anything before continuing.
      </p>

      <div className="amok-review__stats">
        <CustomStatCard
          label="Total Projects"
          value={totalProjects}
          tone="green"
        />
        <CustomStatCard
          label="Active Projects"
          value={activeProjects}
          tone="neutral"
        />
        <CustomStatCard
          label="Ongoing Roles"
          value={roles.length}
          tone="purple"
        />
      </div>

     <div className="amok-review__sections">
       {roles.length > 0 && (
        <CustomSection title="Ongoing Roles" accent="blue">
          {roles.map((r, i) => (
            <CustomEntryCard
              key={i}
              title={r.roleTitle}
              meta={[
                r.projectName,
                r.startDate,
                r.isOngoing ? 'Ongoing' : r.endDate,
                categoryLabels(r.categories),
              ].filter(Boolean)}
              isLoadingDelete={deleting?.type === 'role' && deleting?.index === i}
              onEdit={() => handleEditRole(i)}
              onDelete={() => handleDeleteRole(i)}
              editIconSrc={ICONS.edit}
              deleteIconSrc={ICONS.delete}
            />
          ))}
        </CustomSection>
      )}

      {currentProjects.length > 0 && (
        <CustomSection title="Current Projects" accent="purple">
          {currentProjects.map((p, i) => (
            <CustomEntryCard
              key={i}
              title={p.projectName}
              meta={[
                p.roleTitle,
                p.startDate && `Started ${p.startDate}`,
                p.isOngoing ? 'Ongoing' : p.endDate && `Ended ${p.endDate}`,
                categoryLabels(p.categories),
              ].filter(Boolean)}
              isLoadingDelete={deleting?.type === 'current' && deleting?.index === i}
              onEdit={() => handleEditCurrentProject(i)}
              onDelete={() => handleDeleteCurrentProject(i)}
              editIconSrc={ICONS.edit}
              deleteIconSrc={ICONS.delete}
            />
          ))}
        </CustomSection>
      )}

      {pastProjects.length > 0 && (
        <CustomSection title="Past Projects" accent="yellow">
          {pastProjects.map((p, i) => (
            <CustomEntryCard
              key={i}
              title={p.projectName}
              meta={[
                p.roleTitle,
                p.endDate && `Completed ${p.endDate}`,
                categoryLabels(p.categories),
              ].filter(Boolean)}
              isLoadingDelete={deleting?.type === 'past' && deleting?.index === i}
              onEdit={() => handleEditPastProject(i)}
              onDelete={() => handleDeletePastProject(i)}
              editIconSrc={ICONS.edit}
              deleteIconSrc={ICONS.delete}
            />
          ))}
        </CustomSection>
      )}

      {futureProjects.length > 0 && (
        <CustomSection title="Future Projects" accent="orange">
          {futureProjects.map((p, i) => (
            <CustomEntryCard
              key={i}
              title={p.projectName}
              meta={[
                p.roleTitle,
                p.startDate && `Target ${p.startDate}`,
                p.endDate && `Finished ${p.endDate}`,
                categoryLabels(p.categories),
              ].filter(Boolean)}
              isLoadingDelete={deleting?.type === 'future' && deleting?.index === i}
              onEdit={() => handleEditFutureProject(i)}
              onDelete={() => handleDeleteFutureProject(i)}
              editIconSrc={ICONS.edit}
              deleteIconSrc={ICONS.delete}
            />
          ))}
        </CustomSection>
      )}
     </div>

      <div className="amok-onboarding-step__footer amok-review__footer">
        <CustomButton
          variant="primary"
          size="md"
          trailingIconSrc={ICONS.arrowRight}
          onClick={handleContinue}
        >
          Continue
        </CustomButton>
      </div>
    </div>
  );
};

export default Step4Review;
